<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '../EchoButton.vue';
    import AllFilters from '../filter/AllFilters.vue';
    import type { HideableFilter } from '../filter/types';
    import LoadingDots from '../LoadingDots.vue';
    import Modal from '../Modal.vue';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { useFilterWatch } from '@/js/composables/useFilterWatch';
    import { watch } from 'vue';
    import { CheckIcon, FolderOpenIcon, XIcon } from '@lucide/vue';

    const props = defineProps<{
        show: boolean;
        filterStoreName: FilterStoreName;
        valid?: boolean;
        loading?: boolean;
        showFilters?: boolean;
        hiddenFilters?: HideableFilter[];
        cancelText?: string;
        submitText?: string;
        submitAndOpen?: boolean;
        submitAndOpenText?: string;
    }>();

    defineEmits<{
        (e: 'close'): void;
        (e: 'submit', open?: boolean): void;
    }>();

    const { getFilter } = useFilterWatch(props.filterStoreName, {
        fetchOnInit: false,
    });

    watch(
        () => props.show,
        () => {
            if (props.show) {
                getFilter();
            }
        },
        {
            immediate: true,
        }
    );
</script>
<template>
    <Modal :show="show" :fixed-height="true" :padding="false" @close="$emit('close')">
        <div
            v-if="!loading && show"
            class="flex h-full flex-col overflow-auto xl:flex-row xl:overflow-hidden"
        >
            <div class="p-5 py-8 xl:p-10 xl:overflow-auto w-xl max-w-full shrink-0">
                <slot name="form"></slot>
            </div>
            <div
                class="relative flex-grow flex flex-col md:flex-row border-t xl:border-0 border-gray-light 2xl:border-none"
            >
                <div v-if="showFilters" class="md:w-80">
                    <AllFilters
                        :is-visible="true"
                        :always-visible="true"
                        :filter-store-name="props.filterStoreName"
                        :hidden-filters="props.hiddenFilters"
                    />
                </div>
                <div
                    class="relative border-l border-gray-light flex-grow h-full bg-linear-135 from-primary-beet from-20% to-primary-blueberry min-h-full;"
                >
                    <div class="flex flex-col h-full w-full">
                        <div class="flex-grow flex flex-col min-h-0 overflow-hidden">
                            <slot name="preview"></slot>
                        </div>
                        <div
                            class="flex gap-1 justify-end pr-4 py-4 bg-white border-t border-gray-light"
                        >
                            <EchoButton variant="secondary" @click="$emit('close')">
                                {{ cancelText ?? __('cancel') }}
                                <template #icon-right><XIcon /></template>
                            </EchoButton>
                            <EchoButton
                                variant="primary"
                                :disabled="!valid"
                                :aria-label="submitText ?? __('save')"
                                @click="$emit('submit')"
                            >
                                {{ submitText ?? __('save') }}
                                <template #icon-right>
                                    <slot name="submit-icon"><CheckIcon /></slot>
                                </template>
                            </EchoButton>
                            <EchoButton
                                v-if="submitAndOpen"
                                variant="primary"
                                :disabled="!valid"
                                :aria-label="submitAndOpenText ?? __('save-and-open')"
                                @click="() => $emit('submit', true)"
                            >
                                {{ submitAndOpenText ?? __('save-and-open') }}
                                <template #icon-right><FolderOpenIcon /></template>
                            </EchoButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="loading && show" class="h-full w-full flex items-center justify-center">
            <LoadingDots class="h-96" />
        </div>
    </Modal>
</template>
